import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Agent session status
 */
export const AgentSessionStatusSchema = z.enum([
  "pending",
  "starting",
  "connecting",
  "authenticating",
  "mfa",
  "connected",
  "running",
  "completed",
  "failed",
  "cancelled",
  "expired",
]);

/**
 * Auth state response (safe to return to client)
 */
export const AgentAuthStateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  targetUrl: z.string().url(),
  otpTargetId: z.string().nullable(),
  username: z.string().nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  expiresAt: z.coerce.date().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isExpired: z.boolean(),
});

/**
 * Start AVD login session input
 */
export const StartAVDLoginInputSchema = z.object({
  organizationId: z.string().uuid(),
  /** AVD portal URL */
  avdUrl: z.string().url().default("https://windows.cloud.microsoft.com"),
  /** Azure AD email/username */
  email: z.string().trim().min(1),
  /** Azure AD password */
  password: z.string().min(1),
  /** OTP target ID for MFA (optional if MFA not required) */
  otpTargetId: z.string().optional(),
  /** Windows VM password */
  vmPassword: z.string().min(1),
  /** Desktop name to click (e.g., "AVD-FullDesktop") */
  desktopName: z.string().min(1).max(255),
  /** Name to save auth state as */
  authStateName: z.string().min(1).max(255),
  /** Whether to save auth state on success */
  saveAuthState: z.boolean().default(true),
  /** Run in headless mode */
  headless: z.boolean().default(false),
  /** Optional test instruction to run after login */
  testInstruction: z.string().optional(),
});

/**
 * Start session response
 */
export const StartSessionResponseSchema = z.object({
  sessionId: z.string().uuid(),
  status: AgentSessionStatusSchema,
  message: z.string(),
});

/**
 * Get session status input
 */
export const GetSessionStatusInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

/**
 * Screenshot entry with metadata
 */
export const ScreenshotEntrySchema = z.object({
  /** Base64 encoded screenshot */
  data: z.string(),
  /** Action that triggered this screenshot */
  action: z.string().optional(),
  /** Timestamp */
  timestamp: z.number(),
});

export const SessionMessageEntrySchema = z.object({
  id: z.string(),
  message: z.string(),
  action: z.string().optional(),
  level: z.enum(["info", "warn", "error"]).default("info"),
  timestamp: z.number(),
});

/**
 * Session status response
 */
export const SessionStatusResponseSchema = z.object({
  sessionId: z.string().uuid(),
  status: AgentSessionStatusSchema,
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  /** Latest screenshot (base64) */
  latestScreenshot: z.string().nullable(),
  /** All screenshots captured during the session */
  screenshots: z.array(ScreenshotEntrySchema),
  /** Live agent messages captured during the session */
  messages: z.array(SessionMessageEntrySchema),
  /** Saved auth state ID (if saved) */
  savedAuthStateId: z.string().nullable(),
  /** Saved auth state JSON (for display) */
  savedAuthState: z.string().nullable(),
  /** Live view URL for neko iframe (null when kernel not configured) */
  liveViewUrl: z.string().nullable(),
  /** Document ID for the session recording MP4 (null until recording is uploaded) */
  recordingDocumentId: z.string().nullable(),
});

/**
 * Cancel session input
 */
export const CancelSessionInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

/**
 * Cancel session response
 */
export const CancelSessionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * List auth states input
 */
export const ListAuthStatesInputSchema = z.object({
  organizationId: z.string().uuid(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});

/**
 * List auth states response
 */
export const ListAuthStatesResponseSchema = z.object({
  authStates: z.array(AgentAuthStateSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

/**
 * Delete auth state input
 */
export const DeleteAuthStateInputSchema = z.object({
  organizationId: z.string().uuid(),
  authStateId: z.string().uuid(),
});

/**
 * Delete auth state response
 */
export const DeleteAuthStateResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * Agent Management Contracts
 */
export const agentContracts = {
  // Start a new AVD login session
  startAVDLogin: oc
    .input(StartAVDLoginInputSchema)
    .output(StartSessionResponseSchema)
    .route({
      method: "POST",
    }),

  // Get session status
  getSessionStatus: oc
    .input(GetSessionStatusInputSchema)
    .output(SessionStatusResponseSchema)
    .route({
      method: "GET",
    }),

  // Cancel a running session
  cancelSession: oc
    .input(CancelSessionInputSchema)
    .output(CancelSessionResponseSchema)
    .route({
      method: "POST",
    }),

  // List saved auth states
  listAuthStates: oc
    .input(ListAuthStatesInputSchema)
    .output(ListAuthStatesResponseSchema)
    .route({
      method: "GET",
    }),

  // Delete an auth state
  deleteAuthState: oc
    .input(DeleteAuthStateInputSchema)
    .output(DeleteAuthStateResponseSchema)
    .route({
      method: "DELETE",
    }),
  // Resolve session ID for an agent run (from in-memory session manager)
  getSessionForAgentRun: oc
    .input(
      z.object({
        organizationId: z.string().uuid(),
        agentRunId: z.string().uuid(),
      }),
    )
    .output(
      z.object({
        sessionId: z.string().uuid().nullable(),
      }),
    )
    .route({
      method: "GET",
    }),
} as const;

// Export types
export type AgentSessionStatus = z.infer<typeof AgentSessionStatusSchema>;
export type AgentAuthState = z.infer<typeof AgentAuthStateSchema>;
export type StartAVDLoginInput = z.infer<typeof StartAVDLoginInputSchema>;
export type StartSessionResponse = z.infer<typeof StartSessionResponseSchema>;
export type GetSessionStatusInput = z.infer<typeof GetSessionStatusInputSchema>;
export type SessionStatusResponse = z.infer<typeof SessionStatusResponseSchema>;
export type CancelSessionInput = z.infer<typeof CancelSessionInputSchema>;
export type CancelSessionResponse = z.infer<typeof CancelSessionResponseSchema>;
export type ListAuthStatesInput = z.infer<typeof ListAuthStatesInputSchema>;
export type ListAuthStatesResponse = z.infer<
  typeof ListAuthStatesResponseSchema
>;
export type DeleteAuthStateInput = z.infer<typeof DeleteAuthStateInputSchema>;
export type DeleteAuthStateResponse = z.infer<
  typeof DeleteAuthStateResponseSchema
>;
